import { useId, useState } from 'react';

export interface PercentInputProps {
  id?: string;
  label: string;
  /** Current committed value, 0-100. */
  value: number;
  onChange(value: number, valid: boolean): void;
  hint?: string;
  errorMessage?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  min?: number;
  max?: number;
}

const PERCENT_SHAPE = /^\d{1,3}(\.\d{1,2})?$/;

function normalize(raw: string, min: number, max: number): { value: number; valid: boolean } {
  const trimmed = raw.trim();
  if (trimmed === '' || !PERCENT_SHAPE.test(trimmed)) return { value: Number.NaN, valid: false };
  const n = Number(trimmed);
  return { value: n, valid: Number.isFinite(n) && n >= min && n <= max };
}

/**
 * Collapses "025" to "25" (and "00" to "0") the instant a second digit
 * follows a leading zero, rather than only on blur. A decimal like "0.5"
 * is left alone — the leading zero there is meaningful.
 */
function stripLeadingZeros(s: string): string {
  return /^0+\d/.test(s) ? s.replace(/^0+/, '') : s;
}

/**
 * A percentage field that never goes through `<input type="number">`: that
 * control lets a typed "025" sit in the DOM unnormalized until blur (the bug
 * behind the discount-ceiling screenshot — a plain HTML quirk, not a React
 * one). This is a text input instead, right-aligned with a trailing " %"
 * mask that is purely visual: the underlying value the parent receives is
 * always a clean number, and leading zeros never survive a keystroke.
 */
export function PercentInput({
  id: idProp,
  label,
  value,
  onChange,
  hint,
  errorMessage,
  disabled,
  autoFocus,
  min = 0,
  max = 100,
}: PercentInputProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const [raw, setRaw] = useState(() => String(value));
  const [touched, setTouched] = useState(false);

  const { valid } = normalize(raw, min, max);
  const invalid = touched && raw.trim() !== '' && !valid;

  return (
    <div className="field">
      {/*
       * Same reasoning as MoneyInput: the label is its own element so the
       * trailing "%" affix text never becomes part of its accessible name.
       */}
      <label htmlFor={id}>{label}</label>
      <span className="input-affix input-affix--suffix">
        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={raw}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={invalid}
          onChange={(e) => {
            // Strip anything that isn't a digit or a decimal point as it's
            // typed, then collapse a leading zero immediately — a leading
            // zero never has a chance to linger the way it would in a
            // native number input (the bug behind the "025" screenshot).
            const next = stripLeadingZeros(e.target.value.replace(/[^\d.]/g, ''));
            setRaw(next);
            setTouched(true);
            const result = normalize(next, min, max);
            onChange(result.value, result.valid);
          }}
          onBlur={() => {
            setTouched(true);
            const result = normalize(raw, min, max);
            if (result.valid) setRaw(String(result.value));
          }}
        />
        <span className="input-affix__symbol" aria-hidden="true">
          %
        </span>
      </span>
      {invalid && errorMessage ? (
        <span className="field-error">{errorMessage}</span>
      ) : (
        hint && <span className="field-hint">{hint}</span>
      )}
    </div>
  );
}
